/**
 * Week Calculation Utilities
 * 
 * Calendar-based week system (not ISO 8601):
 * - Week 1 is always the week containing January 1st
 * - Weeks run Monday to Sunday
 * - Week 1 Monday is the Monday on or before January 1st
 */

/**
 * Get the Monday of Week 1 for a given year.
 * Week 1 is defined as the week containing January 1st.
 * 
 * @param {number} year - The year
 * @returns {Date} - The Monday of Week 1
 */
export const getWeek1Monday = (year) => {
  const jan1 = new Date(year, 0, 1);
  const jan1DayOfWeek = jan1.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days to subtract to get to Monday
  // If Jan 1 is Sunday (0), we go back 6 days
  // If Jan 1 is Monday (1), we go back 0 days
  // If Jan 1 is Tuesday (2), we go back 1 day, etc.
  const daysToSubtract = jan1DayOfWeek === 0 ? 6 : jan1DayOfWeek - 1;
  
  const week1Monday = new Date(jan1);
  week1Monday.setDate(jan1.getDate() - daysToSubtract);
  
  return week1Monday;
};

/**
 * Calculate which week number a date falls into for a given year.
 * 
 * @param {Date} date - The date to check
 * @param {number} forYear - The year context for the week calculation
 * @returns {number} - The week number (1-53)
 */
export const getWeekNumber = (date, forYear = null) => {
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);
  
  const year = forYear || targetDate.getFullYear();
  const week1Monday = getWeek1Monday(year);
  week1Monday.setHours(0, 0, 0, 0);
  
  // Calculate days difference
  const diffTime = targetDate.getTime() - week1Monday.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // Calculate week number (1-indexed)
  const weekNumber = Math.floor(diffDays / 7) + 1;
  
  return weekNumber;
};

/**
 * Get the start (Monday) and end (Sunday) dates for a given week.
 * 
 * @param {number} year - The year
 * @param {number} weekNumber - The week number (1-53)
 * @returns {Object} - { start: Date, end: Date, startStr: string, endStr: string }
 */
export const getWeekDateRange = (year, weekNumber) => {
  const week1Monday = getWeek1Monday(year);
  
  // Add (weekNumber - 1) * 7 days to get target week's Monday
  const weekStart = new Date(week1Monday);
  weekStart.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);
  
  // Sunday is 6 days after Monday
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  return {
    start: weekStart,
    end: weekEnd,
    startStr: weekStart.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
    endStr: weekEnd.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
    startISO: weekStart.toISOString().split('T')[0],
    endISO: weekEnd.toISOString().split('T')[0]
  };
};

/**
 * Get the total number of weeks in a year.
 * This is determined by what week December 31st falls into.
 * 
 * @param {number} year - The year
 * @returns {number} - Number of weeks (52 or 53)
 */
export const getWeeksInYear = (year) => {
  const dec31 = new Date(year, 11, 31);
  return getWeekNumber(dec31, year);
};

/**
 * Get the current week and year.
 * 
 * @returns {Object} - { year: number, week: number }
 */
export const getCurrentWeek = () => {
  const today = new Date();
  const year = today.getFullYear();
  const week = getWeekNumber(today, year);
  
  return { year, week };
};

/**
 * Check if a date falls within a week range.
 * Handles year boundary cases where weeks span two calendar years.
 * 
 * @param {Date|string} date - The date to check
 * @param {number} fromYear - Start year
 * @param {number} fromWeek - Start week
 * @param {number} toYear - End year
 * @param {number} toWeek - End week
 * @returns {boolean} - True if date is within range
 */
export const isDateInWeekRange = (date, fromYear, fromWeek, toYear, toWeek) => {
  if (!date) return false;
  
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);
  
  // Get the actual date ranges
  const fromRange = getWeekDateRange(fromYear, fromWeek);
  const toRange = getWeekDateRange(toYear, toWeek);
  
  fromRange.start.setHours(0, 0, 0, 0);
  toRange.end.setHours(23, 59, 59, 999);
  
  return targetDate >= fromRange.start && targetDate <= toRange.end;
};

/**
 * Generate week options for a dropdown selector.
 * 
 * @param {number} year - The year
 * @returns {Array} - Array of { value: number, label: string }
 */
export const generateWeekOptions = (year) => {
  const totalWeeks = getWeeksInYear(year);
  const options = [];
  
  for (let week = 1; week <= totalWeeks; week++) {
    const { startStr, endStr } = getWeekDateRange(year, week);
    options.push({
      value: week,
      label: `Week ${week} (${startStr} - ${endStr})`
    });
  }
  
  return options;
};

/**
 * Get all dates in a week as an array.
 * 
 * @param {number} year - The year
 * @param {number} weekNumber - The week number
 * @returns {Array} - Array of { date: string (YYYY-MM-DD), day: number, dayName: string }
 */
export const getWeekDates = (year, weekNumber) => {
  const { start } = getWeekDateRange(year, weekNumber);
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dates = [];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push({
      date: date.toISOString().split('T')[0],
      day: date.getDate(),
      dayName: dayNames[i],
      fullDate: date
    });
  }
  
  return dates;
};

/**
 * Get year and week from a date.
 * Returns the year context based on which year's week the date falls into.
 * 
 * @param {Date|string} date - The date
 * @returns {Object} - { year: number, week: number }
 */
export const getYearAndWeek = (date) => {
  const targetDate = new Date(date);
  const year = targetDate.getFullYear();
  const week = getWeekNumber(targetDate, year);
  
  // Handle edge cases where the week might belong to adjacent year
  // If we get a very high week number in January, it might be last year's week
  if (targetDate.getMonth() === 0 && week > 50) {
    return { year: year - 1, week: getWeekNumber(targetDate, year - 1) };
  }
  
  // If we get week 1 in December, it might be next year's week 1
  if (targetDate.getMonth() === 11 && week === 1) {
    return { year: year + 1, week: 1 };
  }
  
  return { year, week };
};

export default {
  getWeek1Monday,
  getWeekNumber,
  getWeekDateRange,
  getWeeksInYear,
  getCurrentWeek,
  isDateInWeekRange,
  generateWeekOptions,
  getWeekDates,
  getYearAndWeek
};
